// Path: ./src/app/components/SearchInputField/index.tsx
import { InputAdornment, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Search as SearchIcon } from "@mui/icons-material";
import { messages } from "../../../locales/messages";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Wrapper, StyledTextField, Title } from "./styled";
import { ImageKeywordAutocomplete } from "../ImageKeywordAutocomplete";

interface Props {
  title?: string;
  /**
   * Handles the search action after pressing enter or clicking on the search button.
   */
  onSearch?: (value: string) => void;
  dataTestId?: string;
  clearSearchText?: boolean;
  placeHolder?: string;
  autoFocus?: boolean;
  initialValue?: string;
  /**
   * Whether to enable keyword suggestions for search (currently for images, may extend to other entities in the future)
   * @default false
   */
  enableKeywordSuggestions?: boolean;
}
export function SearchInput({
  title,
  // searchText,
  onSearch,
  dataTestId,
  clearSearchText = false,
  placeHolder,
  autoFocus = false,
  initialValue = "",
  enableKeywordSuggestions = false,
}: Props) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>();

  /**
   * Handles the search action after pressing enter
   */
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      inputRef.current?.blur();
      onSearch && onSearch(searchText);
    }
  };

  useEffect(() => {
    if (clearSearchText) {
      setSearchText("");
    }
  }, [clearSearchText]);

  useEffect(() => {
    setSearchText(initialValue);
  }, [initialValue]);

  // If keyword suggestions are enabled, use the ImageKeywordAutocomplete component
  if (enableKeywordSuggestions) {
    return (
      <ImageKeywordAutocomplete
        title={title}
        placeholder={placeHolder || (t(messages.Search()) as string)}
        initialValue={initialValue}
        autoFocus={autoFocus}
        clearSearchText={clearSearchText}
        dataTestId={dataTestId}
        dataTestIdInput={`${dataTestId}-input`}
        dataTestIdSearchButton={`${dataTestId}-button`}
        onSearch={onSearch}
        onSuggestionSelect={onSearch}
        styleVariant="default"
        showSearchButton={true}
        showSuggestions={true}
      />
    );
  }

  return (
    <Wrapper data-testid="searchinput-test-id">
      {title && <Title>{title}</Title>}
      <StyledTextField
        inputRef={inputRef}
        autoFocus={autoFocus}
        data-testid={dataTestId}
        id="search-input"
        placeholder={
          placeHolder ? placeHolder : (t(messages.Search()) as string)
        }
        fullWidth
        value={searchText}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const text = e.target.value;
          setSearchText(text);
        }}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  onSearch && onSearch(searchText);
                }}
                data-testid={`${dataTestId}-button`}
              >
                <SearchIcon
                  style={{
                    color: "#fd954f",
                    fontSize: 34,
                  }}
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Wrapper>
  );
}
